import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/articles';

export default function About() {
  const pageTitle = '关于我们 - 淫母日记 | 品牌介绍、读者口碑与联系方式';
  const pageDesc = '了解淫母日记的品牌故事与发展历程，查看读者真实口碑评价，获取联系方式。淫母日记是国内领先的成人日记文学阅读平台。';

  const faqSchemaData = useMemo(() => ({
    mainEntity: [
      {
        '@type': 'Question',
        name: '淫母日记是一个什么样的平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '淫母日记是国内领先的成人日记文学阅读平台，致力于为成年读者提供高质量的原创日记文学作品，涵盖日记系列、短篇故事、连载小说等多种类型。我们的编辑团队严格把控每一篇作品的质量，确保为读者提供优质的阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系淫母日记的编辑团队？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下方式联系我们：电子邮箱 contact@elinggong.cn，微信公众号 yinfumuji_official，或在工作时间（周一至周五 9:00-18:00）拨打客服电话。我们位于上海市浦东新区张江高科技园区。',
        },
      },
      {
        '@type': 'Question',
        name: '淫母日记接受投稿吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，淫母日记欢迎优秀作者投稿。如果您有高质量的成人日记文学作品，可以通过我们的联系方式提交稿件。编辑团队会在收到稿件后的5个工作日内给予回复。',
        },
      },
    ],
  }), []);

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords="淫母日记,品牌介绍,关于我们,读者口碑,联系方式,成人日记文学"
        canonicalUrl="https://elinggong.cn/about"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 淫母日记',
          description: pageDesc,
          url: 'https://elinggong.cn/about',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: '关于我们' },
                ]}
              />
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-50 mb-2">
              关于我们
            </h1>
            <p className="text-sm text-foreground-500 mb-10">
              了解淫母日记的品牌故事与团队理念
            </p>

            <section className="mb-14">
              <h2 className="text-xl font-heading font-semibold text-foreground-100 mb-5">
                品牌介绍
              </h2>
              <div className="prose prose-invert max-w-none
                prose-p:text-foreground-300 prose-p:leading-loose prose-p:text-base prose-p:mb-4 prose-p:indent-8
                prose-strong:text-foreground-100">
                {aboutData.brandIntro.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph.trim()}</p>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl font-heading font-semibold text-foreground-100 mb-5">
                读者口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aboutData.reputation.map((item, index) => (
                  <div key={index} className="bg-background-100 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/20 text-accent-400 font-label text-sm font-medium">
                        {item.name.charAt(item.name.length - 1)}
                      </div>
                      <div>
                        <p className="text-sm font-label font-medium text-foreground-200">
                          {item.name}
                        </p>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-[10px] ${
                                i < item.rating
                                  ? 'ri-star-fill text-accent-500'
                                  : 'ri-star-fill text-foreground-700'
                              }`}
                            ></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-400 leading-relaxed">
                      {item.comment}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl font-heading font-semibold text-foreground-100 mb-5">
                联系方式
              </h2>
              <div className="bg-background-100 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-400 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-label font-medium text-foreground-200 mb-1">
                        电子邮箱
                      </h4>
                      <p className="text-sm text-foreground-400">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/15 text-accent-400 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-label font-medium text-foreground-200 mb-1">
                        微信公众号
                      </h4>
                      <p className="text-sm text-foreground-400">{aboutData.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-500/15 text-secondary-400 flex-shrink-0">
                      <i className="ri-qq-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-label font-medium text-foreground-200 mb-1">
                        QQ
                      </h4>
                      <p className="text-sm text-foreground-400">{aboutData.contact.qq}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-400 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-label font-medium text-foreground-200 mb-1">
                        公司地址
                      </h4>
                      <p className="text-sm text-foreground-400">{aboutData.contact.address}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-background-200/60 flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/15 text-accent-400 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-label font-medium text-foreground-200 mb-1">
                      工作时间
                    </h4>
                    <p className="text-sm text-foreground-400">{aboutData.contact.workingHours}</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-foreground-100 mb-5">
                常见问题
              </h2>
              <SchemaOrg type="FAQPage" data={faqSchemaData} />
              <div className="flex flex-col gap-3">
                <div className="bg-background-100 rounded-lg p-5">
                  <h4 className="text-sm font-label font-medium text-foreground-200 mb-2">
                    淫母日记是一个什么样的平台？
                  </h4>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    淫母日记是国内领先的成人日记文学阅读平台，致力于为成年读者提供高质量的原创日记文学作品，涵盖日记系列、短篇故事、连载小说等多种类型。我们的编辑团队严格把控每一篇作品的质量，确保为读者提供优质的阅读体验。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-5">
                  <h4 className="text-sm font-label font-medium text-foreground-200 mb-2">
                    如何联系淫母日记的编辑团队？
                  </h4>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    您可以通过以下方式联系我们：电子邮箱 contact@elinggong.cn，微信公众号 yinfumuji_official，或在工作时间（周一至周五 9:00-18:00）拨打客服电话。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-5">
                  <h4 className="text-sm font-label font-medium text-foreground-200 mb-2">
                    淫母日记接受投稿吗？
                  </h4>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    是的，淫母日记欢迎优秀作者投稿。如果您有高质量的成人日记文学作品，可以通过我们的联系方式提交稿件。编辑团队会在收到稿件后的5个工作日内给予回复。
                  </p>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}